<template>
  <div
    class="mx-auto bg-white p-6 rounded-2xl shadow-lg space-y-6 md:space-y-4"
    role="note"
    aria-labelledby="rules-triggered-title"
    aria-live="polite">
    <!-- Header -->
    <header class="flex items-center space-x-2">
      <h3 id="rules-triggered-title" class="text-xl md:text-2xl font-semibold text-gray-800">
        {{ $t("triggeredRecommendations.hereIsWhy") }}
      </h3>
    </header>

    <!-- CRO Bridge Paragraph -->
    <p class="text-base text-gray-600 leading-relaxed italic">
      {{ $t(dynamicCroBridgeKey) }}
    </p>

    <!-- Budget-based Recommendations Section -->
    <div v-if="budgetBasedRecommendations.length > 0" class="space-y-3">
      <h4 class="font-semibold text-gray-800 text-lg">{{ $t("triggeredRecommendations.budgetBasedTitle") }}</h4>
      <p class="text-sm text-gray-600 italic">{{ getBudgetExplanation() }}</p>
      <ul class="space-y-2 text-base text-gray-700">
        <li v-for="(entry, index) in budgetBasedRecommendations" :key="entry.key" class="flex items-start space-x-2">
          <span class="flex-none mt-1 text-indigo-500">•</span>
          <div class="transition-opacity duration-300 opacity-70 hover:opacity-100">
            <p>{{ entry.text }}</p>
          </div>
        </li>
      </ul>
    </div>

    <!-- Goal-based Recommendations Section -->
    <div v-if="goalBasedRecommendations.length > 0" class="space-y-3">
      <h4 class="font-semibold text-gray-800 text-lg">{{ $t("triggeredRecommendations.goalBasedTitle") }}</h4>
      <p class="text-sm text-gray-600 italic">{{ getGoalExplanation() }}</p>
      <ul class="space-y-2 text-base text-gray-700">
        <li v-for="(entry, index) in goalBasedRecommendations" :key="entry.key" class="flex items-start space-x-2">
          <span class="flex-none mt-1 text-green-500">•</span>
          <div class="transition-opacity duration-300 opacity-70 hover:opacity-100">
            <p>{{ entry.text }}</p>
          </div>
        </li>
      </ul>
    </div>

    <!-- Fallback for generic recommendations -->
    <div v-if="budgetBasedRecommendations.length === 0 && goalBasedRecommendations.length === 0" class="space-y-3">
      <ul class="space-y-2 text-base text-gray-700">
        <li v-for="(entry, index) in recommendations" :key="entry.key" class="flex items-start space-x-2">
          <span class="flex-none mt-1 text-indigo-500">•</span>
          <div class="transition-opacity duration-300 opacity-70 hover:opacity-100">
            <p>{{ entry.text }}</p>
          </div>
        </li>
      </ul>
    </div>

    <!-- Fallback button for generic recommendations -->
    <div v-if="!hasPersonalizedRecommendations" class="pt-4 border-t border-gray-100">
      <p class="text-sm text-gray-500 mb-3">
        {{ $t("triggeredRecommendations.fallback.description") }}
      </p>
      <RouterLink
        to="/get-started"
        class="inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors duration-200 text-sm font-medium">
        {{ $t("triggeredRecommendations.fallback.buttonText") }}
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { useFormStore } from "@/features/blueprint/stores/useFormStore";
import { usePlanSelectionStore } from "@/features/planSelection/stores/usePlanSelectionStore";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  recommendations: Array<{
    key: string;
    text: string;
  }>;
}>();

const formStore = useFormStore();
const planStore = usePlanSelectionStore();
const { t } = useI18n();

// Check if user has SmartLeadForm recommendations
const hasPersonalizedRecommendations = computed(() => {
  return formStore.answers.recommendedProducts && Array.isArray(formStore.answers.recommendedProducts);
});

// Determine if user came through SmartLeadForm, has URL preselections, or directly
const dynamicCroBridgeKey = computed(() => {
  // Check if user has URL preselected plans
  const hasUrlPreselections = planStore.urlPreselectedPlans.length > 0;

  return hasPersonalizedRecommendations.value || hasUrlPreselections
    ? "customPackage.croBridgeBodyPersonalized"
    : "customPackage.croBridgeBodyGeneric";
});

// Budget-based recommendations (core packages based on budget tier)
const budgetBasedRecommendations = computed(() => {
  if (!hasPersonalizedRecommendations.value) return [];

  const budget = typeof formStore.answers.budget === "string" ? formStore.answers.budget : "starter";
  const budgetPackages = getBudgetBasedPackages(budget);
  const budgetReasonKeys = getBudgetReasonKeys(budgetPackages);

  return props.recommendations.filter(rec => budgetReasonKeys.includes(rec.key as any));
});

// Goal-based recommendations (additional packages based on specific goals/situations)
const goalBasedRecommendations = computed(() => {
  if (!hasPersonalizedRecommendations.value) return [];

  const budget = typeof formStore.answers.budget === "string" ? formStore.answers.budget : "starter";
  const budgetPackages = getBudgetBasedPackages(budget);
  const budgetReasonKeys = getBudgetReasonKeys(budgetPackages);

  return props.recommendations.filter(rec => !budgetReasonKeys.includes(rec.key as any));
});

function getBudgetBasedPackages(budget: string): string[] {
  switch (budget) {
    case "starter":
      return ["webHosting_total", "domain_managed", "branding_mini", "techConcierge_extra_5h", "techConcierge_free"];
    case "professional":
      return ["webHosting_pro", "growthCall_monthly", "branding_full", "seo_booster"];
    case "premium":
      return [
        "webHosting_pro",
        "growthCall_monthly",
        "humanContent_business",
        "branding_premium",
        "cro_booster",
        "seo_booster",
        "content_strategy",
      ];
    default:
      return [];
  }
}

function getBudgetReasonKeys(packageIds: string[]): string[] {
  const reasonKeys: string[] = [];

  packageIds.forEach(packageId => {
    // Map package IDs to their reason keys based on useRecommendation.ts logic
    switch (packageId) {
      case "webHosting_total":
        reasonKeys.push("triggeredRecommendations.build.standardPackage");
        break;
      case "webHosting_pro":
        reasonKeys.push("triggeredRecommendations.build.ecommercePro");
        break;
      case "domain_managed":
        reasonKeys.push("triggeredRecommendations.build.domainManaged");
        break;
      case "branding_mini":
        reasonKeys.push("triggeredRecommendations.direct.brandRefresh");
        break;
      case "branding_full":
        reasonKeys.push("triggeredRecommendations.direct.brandRefresh");
        break;
      case "branding_premium":
        reasonKeys.push("triggeredRecommendations.goal.improveBrand");
        break;
      case "seo_booster":
        // This can have multiple reason keys based on answers, but we'll include the common ones
        reasonKeys.push("triggeredRecommendations.direct.seoOpportunity");
        reasonKeys.push("triggeredRecommendations.goal.moreLeads");
        break;
      case "growthCall_monthly":
        reasonKeys.push("triggeredRecommendations.content.monthlyGrowthCall");
        break;
      case "humanContent_business":
        reasonKeys.push("triggeredRecommendations.content.blogSeoAi");
        reasonKeys.push("triggeredRecommendations.content.crmIntegration");
        reasonKeys.push("triggeredRecommendations.involvement.handsOff");
        break;
      case "cro_booster":
        reasonKeys.push("triggeredRecommendations.optimize.professionalUx");
        reasonKeys.push("triggeredRecommendations.focus.ecommerce");
        break;
      case "techConcierge_extra_5h":
        reasonKeys.push("triggeredRecommendations.tech.contentSupport1h");
        reasonKeys.push("triggeredRecommendations.involvement.handsOff");
        reasonKeys.push("triggeredRecommendations.urgency.immediate");
        break;
      case "techConcierge_free":
        reasonKeys.push("triggeredRecommendations.tech.conciergeFree");
        break;
      case "techConcierge_5h":
        reasonKeys.push("triggeredRecommendations.tech.contentSupport1h");
        reasonKeys.push("triggeredRecommendations.goal.maintenance");
        break;
      case "content_strategy":
        reasonKeys.push("triggeredRecommendations.content.keywordResearch");
        break;
    }
  });

  return reasonKeys;
}

function getBudgetExplanation(): string {
  const budget = typeof formStore.answers.budget === "string" ? formStore.answers.budget : "starter";
  switch (budget) {
    case "starter":
      return t("triggeredRecommendations.budgetExplanation.starter");
    case "professional":
      return t("triggeredRecommendations.budgetExplanation.professional");
    case "premium":
      return t("triggeredRecommendations.budgetExplanation.premium");
    default:
      return "";
  }
}

function getGoalExplanation(): string {
  const goal = formStore.answers.goal;
  const situation = formStore.answers.situation;

  if (goal === "getClients") {
    return t("triggeredRecommendations.goalExplanation.getClients");
  } else if (situation === "haveWebsite") {
    return t("triggeredRecommendations.goalExplanation.haveWebsite");
  } else if (formStore.answers.involvement === "handsOff") {
    return t("triggeredRecommendations.goalExplanation.handsOff");
  }

  return t("triggeredRecommendations.goalExplanation.default");
}
</script>
